import {Connection, ConnectionType, Meter, MeterType, Organisation} from '@flowmaps-com/flowmaps-typescriptmodels';
import {Entity} from '../../../handlers/entity';

export const CALCULATED_RESIDUAL_METER_PREFIX = 'calculated-residual-';
export const CALCULATED_RESIDUAL_SOURCE_PREFIX = 'VIRTUAL-DELTA-';
export const CALCULATED_RESIDUAL_COLOR = '#7568c9';

export function calculatedResidualSourceMeterNumber(connectionId?: string): string {
  return connectionId ? `${CALCULATED_RESIDUAL_SOURCE_PREFIX}${connectionId}` : CALCULATED_RESIDUAL_SOURCE_PREFIX;
}

export function isCalculatedResidual(meter?: Meter): boolean {
  return !!meter?.meterId?.startsWith(CALCULATED_RESIDUAL_METER_PREFIX);
}

export function isPercentageAllocated(meter?: Meter): boolean {
  return meter?.allocationPercentage != null;
}

export function isMeterVisible(meter?: Meter): boolean {
  return (meter?.details as any)?.visible !== false;
}

export function meterDisplayParentId(meter?: Meter): string | undefined {
  return (meter?.details as any)?.displayParentId || undefined;
}

export interface MeterDisplayHierarchy {
  visibleIds: Set<string>;
  parentIds: Map<string, string>;
  childIds: Map<string, string[]>;
  rootIds: string[];
}

export function buildMeterDisplayHierarchy(meters: Meter[] = []): MeterDisplayHierarchy {
  const metersById = new Map((meters || [])
    .filter(candidate => !!candidate?.meterId)
    .map(candidate => [candidate.meterId, candidate]));
  const displayParentId = (meter?: Meter) => meterDisplayParentId(meter);
  const isDisplayVisible = (meter?: Meter) => isMeterVisible(meter);
  const visibleIds = new Set((meters || [])
    .filter(meter => meter?.meterId && isDisplayVisible(meter))
    .map(meter => meter.meterId));
  const parentIds = new Map<string, string>();
  const childIds = new Map<string, string[]>();
  const rootIds: string[] = [];
  const resolveVisibleParent = (parentId: string | undefined, visited: Set<string>): string | undefined => {
    if (!parentId || visited.has(parentId)) return undefined;
    const parent = metersById.get(parentId);
    if (!parent) return undefined;
    visited.add(parentId);
    if (isDisplayVisible(parent)) return parent.meterId;
    return resolveVisibleParent(displayParentId(parent), visited)
      || resolveVisibleParent(parent.parentId, visited);
  };
  (meters || []).filter(meter => meter?.meterId && isDisplayVisible(meter)).forEach(meter => {
    const parentId = resolveVisibleParent(displayParentId(meter), new Set([meter.meterId]))
      || resolveVisibleParent(meter.parentId, new Set([meter.meterId]));
    if (parentId) {
      parentIds.set(meter.meterId, parentId);
      const children = childIds.get(parentId) || [];
      children.push(meter.meterId);
      childIds.set(parentId, children);
    } else {
      rootIds.push(meter.meterId);
    }
  });
  return {visibleIds, parentIds, childIds, rootIds};
}

export function effectiveMeterParentId(meter: Meter | undefined, meters: Meter[] = []): string | undefined {
  if (!meter?.meterId) return undefined;
  return buildMeterDisplayHierarchy(meters).parentIds.get(meter.meterId);
}

export function effectiveMeterChildIds(meter: Meter | undefined, meters: Meter[] = []): string[] {
  if (!meter?.meterId) return [];
  return buildMeterDisplayHierarchy(meters).childIds.get(meter.meterId) || [];
}

export function effectiveMeterRootIds(meters: Meter[] = []): string[] {
  return buildMeterDisplayHierarchy(meters).rootIds;
}

export function effectiveMeterType(meter?: Meter): MeterType | undefined {
  if (!meter) return undefined;
  if ([MeterType.GROSS_PRODUCTION, MeterType.COOLING].includes(meter.info?.type)) return meter.info.type;
  return meter.details?.type || meter.info?.type;
}

export function isChargePointMeter(entity?: Entity): boolean {
  if (!entity?.meter || isCalculatedResidual(entity.meter)) return false;
  return entity.connection?.info?.connectionType === ConnectionType.ChargePoint
    || (entity.meter.details as any)?.chargePoint === true;
}

export function calculatedResidualEntity(organisation: Organisation, location: any,
                                         connection: Connection): Entity | null {
  if (!organisation || !location || !connection) return null;
  const existing = connection.meters?.find(isCalculatedResidual);
  if (existing) return new Entity(organisation, location, connection, existing);
  const hasSourceSubmeter = connection.meters?.some(meter => meter.active !== false
    && meter.info?.type === MeterType.INTERMEDIATE && !isCalculatedResidual(meter));
  if (!hasSourceSubmeter) return null;
  const primary = connection.meters?.find(meter => meter.active !== false && meter.info?.type === MeterType.PRIMARY)
    || connection.meters?.[0];
  if (!primary) return null;
  const meter = <Meter><unknown>{
    meterId: `${CALCULATED_RESIDUAL_METER_PREFIX}${connection.connectionId}`,
    info: {
      accessPointId: calculatedResidualSourceMeterNumber(connection.connectionId),
      type: MeterType.INTERMEDIATE,
      surveyor: primary.info.surveyor,
      label: 'Unallocated consumption',
      virtual: true,
      meterNumbers: []
    },
    details: {type: MeterType.INTERMEDIATE, visible: true, chargePoint: false, meterNumbers: [], tags: []},
    active: true,
    parentId: primary.meterId
  };
  return new Entity(organisation, location, connection, meter);
}

export function hasActiveSourceBpm(location: any, excludedMeterId?: string): boolean {
  return (location?.connections || []).some((connection: Connection) => connection.meters?.some(meter =>
    meter.meterId !== excludedMeterId && meter.active !== false
    && meter.info?.type === MeterType.GROSS_PRODUCTION));
}

export function hasActiveSourceChargePoint(location: any, excludedMeterId?: string): boolean {
  return (location?.connections || []).some((connection: Connection) =>
    connection.info?.connectionType === ConnectionType.ChargePoint
    && connection.meters?.some(meter => meter.meterId !== excludedMeterId && meter.active !== false));
}
